import { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { Activity, Hand, Heart, Dumbbell, Trophy, ArrowRight, Star, Phone, Mail, MapPin, BadgeCheck, Calendar, Car, Clock } from "lucide-react";
import { Reveal, MaskedLine } from "../components/Reveal";
import { Marquee } from "../components/Marquee";
import { BookLink } from "../components/BookLink";
import { SERVICES, AUDIENCES, SERVICE_AREAS, PHONE, PHONE_HREF, EMAIL, EMAIL_HREF } from "../config";

const serviceIcons = [Activity, Hand, Heart, Dumbbell, Trophy];

const HERO_IMG = "https://images.unsplash.com/photo-1540205895360-4ad4cffb3aa8?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400";
const DR_LANE_IMG = "/assets/dr-lane.jpg";

export default function Home() {
    const { scrollY } = useScroll();
    const heroImgY = useTransform(scrollY, [0, 700], [0, 90]);
    const heroFloatY = useTransform(scrollY, [0, 700], [0, -60]);

    useEffect(() => {
        document.title = "Chirolane & Wellness, LLC | Move Better. Live Stronger. | DFW Chiropractor";
    }, []);

    return (
        <main>
            {/* HERO */}
            <section className="hero-grain relative overflow-hidden bg-gradient-to-b from-blue-50/70 via-slate-50 to-white pt-28 sm:pt-36 pb-20 sm:pb-28">
                <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 grid grid-cols-1 lg:grid-cols-12 gap-14 lg:gap-10 items-center">
                    <div className="lg:col-span-7 relative z-10">
                        <motion.p
                            initial={{ opacity: 0, y: 16 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6, delay: 0.15 }}
                            className="text-xs uppercase tracking-[0.3em] font-semibold text-blue-800 mb-6 flex items-center gap-3"
                        >
                            <span className="h-px w-10 bg-blue-700 inline-block" />
                            Chiropractic &amp; Wellness · South Dallas &amp; Surrounding Areas
                        </motion.p>
                        <h1 data-testid="hero-title" className="font-display text-5xl sm:text-6xl lg:text-7xl font-medium tracking-tight leading-[1.05] text-[#0A192F]">
                            <MaskedLine delay={0.25}>Move Better.</MaskedLine>
                            <MaskedLine delay={0.4} className="italic text-blue-700">Live Stronger.</MaskedLine>
                        </h1>
                        <motion.p
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.7, delay: 0.7 }}
                            className="mt-7 text-base sm:text-lg font-light leading-relaxed text-slate-600 max-w-xl"
                        >
                            Premium, personalized chiropractic care led by Dr. Lane — helping athletes, families, and active adults across South Dallas, Oak Cliff, DeSoto, Lancaster, and surrounding communities restore movement, relieve pain, and build lasting strength.
                        </motion.p>
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.7, delay: 0.85 }}
                            className="mt-9 flex flex-col sm:flex-row gap-4"
                        >
                            <BookLink
                                data-testid="hero-book-button"
                                className="inline-flex items-center justify-center gap-2 rounded-full bg-blue-700 text-white text-sm font-semibold px-8 py-4 transition-all duration-300 hover:bg-[#0A192F] hover:shadow-xl hover:-translate-y-0.5"
                            >
                                <Calendar className="h-4 w-4" />
                                Book an Appointment
                            </BookLink>
                            <Link
                                to="/services"
                                data-testid="hero-explore-services-button"
                                className="inline-flex items-center justify-center gap-2 rounded-full border border-slate-300 text-[#0A192F] text-sm font-semibold px-8 py-4 transition-all duration-300 hover:border-blue-700 hover:text-blue-700"
                            >
                                Explore Services
                                <ArrowRight className="h-4 w-4" />
                            </Link>
                        </motion.div>
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ duration: 0.8, delay: 1.05 }}
                            className="mt-12 flex items-center gap-5 text-sm text-slate-500"
                        >
                            <span className="flex items-center gap-2"><BadgeCheck className="h-4 w-4 text-blue-700" /> Dr. Lane, DC</span>
                            <span className="h-4 w-px bg-slate-300" />
                            <span className="flex items-center gap-2">
                                <Star className="h-4 w-4 text-amber-400 fill-amber-400" /> 5.0 Patient Rating
                            </span>
                        </motion.div>
                        <motion.div
                            initial={{ opacity: 0, y: 16 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.7, delay: 1.2 }}
                            className="mt-6 flex flex-col sm:flex-row gap-3"
                        >
                            <span data-testid="hero-mobile-care-badge" className="inline-flex items-center gap-2.5 rounded-2xl bg-blue-700 text-white px-5 py-3.5 text-sm font-semibold shadow-lg shadow-blue-700/20">
                                <Car className="h-4 w-4 shrink-0" />
                                Mobile care — we come to you across DFW
                            </span>
                            <span data-testid="hero-weekend-hours-badge" className="inline-flex items-center gap-2.5 rounded-2xl border border-amber-200 bg-amber-50 text-amber-800 px-5 py-3.5 text-sm font-semibold">
                                <Clock className="h-4 w-4 shrink-0" />
                                Now booking weekends only — Saturday &amp; Sunday
                            </span>
                        </motion.div>
                    </div>
                    <div className="lg:col-span-5 relative">
                        <motion.div
                            initial={{ opacity: 0, scale: 0.94 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ duration: 1.1, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
                            className="relative"
                        >
                            <motion.div style={{ y: heroImgY }} className="rounded-[2rem] overflow-hidden shadow-2xl shadow-blue-900/20 aspect-[4/5] max-w-md mx-auto">
                                <img src={HERO_IMG} alt="Chiropractic adjustment session at Chirolane & Wellness" className="h-full w-full object-cover scale-110" />
                            </motion.div>
                            <motion.div
                                style={{ y: heroFloatY }}
                                initial={{ opacity: 0, y: 30 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.8, delay: 1.1 }}
                                className="absolute -bottom-6 -left-2 sm:left-2 bg-white/85 backdrop-blur-xl border border-white/60 shadow-xl rounded-2xl px-6 py-5"
                            >
                                <p className="font-display italic text-xl text-[#0A192F]">“Your body was built to move.”</p>
                                <p className="text-xs uppercase tracking-[0.2em] text-blue-700 font-semibold mt-1.5">Dr. Lane, DC</p>
                            </motion.div>
                        </motion.div>
                    </div>
                </div>
            </section>

            <Marquee items={["Move Better. Live Stronger.", ...SERVICE_AREAS]} />

            {/* SERVICES OVERVIEW */}
            <section className="py-20 sm:py-28 lg:py-32 bg-white">
                <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16">
                    <Reveal className="max-w-2xl">
                        <p className="text-xs uppercase tracking-[0.3em] font-semibold text-blue-800 mb-5">Our Care</p>
                        <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-medium tracking-tight text-[#0A192F] leading-tight">
                            Care designed around <span className="italic text-blue-700">how you live</span>
                        </h2>
                    </Reveal>
                    <div data-testid="services-grid" className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {SERVICES.map((s, i) => {
                            const Icon = serviceIcons[i];
                            return (
                                <Reveal key={s.id} delay={i * 0.08}>
                                    <Link
                                        to={`/services#${s.id}`}
                                        data-testid={s.testid}
                                        className="group block h-full rounded-3xl border border-slate-200 bg-white p-8 transition-all duration-300 hover:-translate-y-1.5 hover:shadow-xl hover:shadow-blue-900/10 hover:border-blue-200"
                                    >
                                        <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-blue-50 text-blue-700 transition-colors duration-300 group-hover:bg-blue-700 group-hover:text-white">
                                            <Icon className="h-5 w-5" />
                                        </span>
                                        <h3 className="mt-6 font-display text-2xl font-semibold text-[#0A192F]">{s.title}</h3>
                                        <p className="mt-2 text-sm leading-relaxed text-slate-500">{s.tagline}.</p>
                                        <span className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-blue-700">
                                            Learn more
                                            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1.5" />
                                        </span>
                                    </Link>
                                </Reveal>
                            );
                        })}
                        <Reveal delay={0.4}>
                            <BookLink
                                data-testid="services-overview-book-card"
                                className="group flex h-full flex-col justify-between rounded-3xl bg-[#0A192F] p-8 text-white transition-all duration-300 hover:-translate-y-1.5 hover:shadow-xl hover:shadow-blue-900/30"
                            >
                                <div>
                                    <p className="font-display italic text-3xl leading-snug">Not sure where to start?</p>
                                    <p className="mt-3 text-sm text-slate-400 leading-relaxed">
                                        Book a consultation and Dr. Lane will map the right care plan for your body.
                                    </p>
                                </div>
                                <span className="mt-8 inline-flex items-center gap-2 text-sm font-semibold text-sky-300">
                                    Book an Appointment
                                    <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1.5" />
                                </span>
                            </BookLink>
                        </Reveal>
                    </div>
                </div>
            </section>

            {/* AUDIENCE BENEFITS BENTO */}
            <section className="py-20 sm:py-28 lg:py-32 bg-slate-50">
                <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16">
                    <Reveal className="max-w-2xl">
                        <p className="text-xs uppercase tracking-[0.3em] font-semibold text-blue-800 mb-5">Who We Serve</p>
                        <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-medium tracking-tight text-[#0A192F] leading-tight">
                            Care for every <span className="italic text-blue-700">body</span>, at every stage
                        </h2>
                    </Reveal>
                    <div data-testid="audience-benefits-grid" className="mt-14 grid grid-cols-1 lg:grid-cols-12 gap-6">
                        {AUDIENCES.map((a, i) => (
                            <Reveal key={a.title} delay={i * 0.08} className={`col-span-1 ${a.span}`}>
                                <div className={`group relative h-full min-h-[240px] rounded-3xl overflow-hidden transition-all duration-300 hover:-translate-y-1.5 hover:shadow-xl hover:shadow-blue-900/10 ${a.image ? "" : "bg-white border border-slate-200"}`}>
                                    {a.image && (
                                        <>
                                            <img src={a.image} alt={`${a.title} chiropractic care in DFW`} className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                                            <div className="absolute inset-0 bg-gradient-to-t from-[#0A192F]/90 via-[#0A192F]/40 to-transparent" />
                                        </>
                                    )}
                                    <div className={`relative p-8 sm:p-10 flex flex-col justify-end h-full ${a.image ? "min-h-[280px]" : "min-h-[240px]"}`}>
                                        <h3 className={`font-display text-2xl sm:text-3xl font-semibold ${a.image ? "text-white" : "text-[#0A192F]"}`}>{a.title}</h3>
                                        <p className={`mt-3 text-sm leading-relaxed max-w-md ${a.image ? "text-slate-200" : "text-slate-500"}`}>{a.text}</p>
                                    </div>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            {/* DR. LANE TRUST SECTION */}
            <section className="py-20 sm:py-28 lg:py-32 bg-white overflow-hidden">
                <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 grid grid-cols-1 lg:grid-cols-12 gap-14 items-center">
                    <Reveal className="lg:col-span-5">
                        <div className="relative max-w-md">
                            <div className="absolute -inset-4 rounded-[2.5rem] bg-blue-50 -rotate-2" />
                            <img
                                data-testid="dr-lane-headshot"
                                src={DR_LANE_IMG}
                                alt="Dr. Lane, DC — Chirolane & Wellness"
                                className="relative rounded-[2rem] aspect-[4/5] w-full object-cover shadow-xl shadow-blue-900/10"
                            />
                        </div>
                    </Reveal>
                    <div className="lg:col-span-7">
                        <Reveal>
                            <p className="text-xs uppercase tracking-[0.3em] font-semibold text-blue-800 mb-5">Meet Your Doctor</p>
                            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-medium tracking-tight text-[#0A192F] leading-tight">
                                “Movement is medicine — <span className="italic text-blue-700">my job is to help you access it.</span>”
                            </h2>
                        </Reveal>
                        <Reveal delay={0.1}>
                            <p className="mt-7 text-base sm:text-lg font-light leading-relaxed text-slate-600 max-w-2xl">
                                Dr. Quandacia Lane, D.C., M.S. — licensed chiropractor, movement specialist, and proud USMC veteran — founded Chirolane &amp; Wellness on a simple belief: lasting relief comes from treating the root cause, not chasing symptoms. Her mobile practice brings personalized chiropractic care, manual therapy, and corrective exercise directly to patients across DFW.
                            </p>
                        </Reveal>
                        <Reveal delay={0.18}>
                            <div className="mt-9 grid grid-cols-3 gap-6 max-w-lg">
                                {[["D.C., M.S.", "Parker University"], ["USMC", "Veteran-owned"], ["DFW", "Mobile practice"]].map(([big, small]) => (
                                    <div key={small}>
                                        <p className="font-display text-3xl sm:text-4xl font-semibold text-blue-700">{big}</p>
                                        <p className="mt-1 text-xs uppercase tracking-[0.15em] text-slate-500">{small}</p>
                                    </div>
                                ))}
                            </div>
                        </Reveal>
                        <Reveal delay={0.25}>
                            <Link
                                to="/about"
                                data-testid="home-about-dr-lane-link"
                                className="mt-10 inline-flex items-center gap-2 text-sm font-semibold text-blue-700 hover:text-[#0A192F] transition-colors"
                            >
                                Read Dr. Lane's story
                                <ArrowRight className="h-4 w-4" />
                            </Link>
                        </Reveal>
                    </div>
                </div>
            </section>

            {/* CONTACT STRIP */}
            <section className="py-20 sm:py-28 bg-gradient-to-b from-blue-50/70 to-white">
                <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
                    <Reveal className="lg:col-span-7">
                        <p className="text-xs uppercase tracking-[0.3em] font-semibold text-blue-800 mb-5">Serving South Dallas &amp; Surrounding Areas</p>
                        <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-medium tracking-tight text-[#0A192F] leading-tight">
                            Your stronger life starts with <span className="italic text-blue-700">one visit</span>
                        </h2>
                        <div className="mt-7 flex flex-wrap gap-2.5">
                            {SERVICE_AREAS.map((area) => (
                                <span key={area} className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-4 py-2 text-xs font-medium text-slate-600">
                                    <MapPin className="h-3 w-3 text-blue-700" /> {area}
                                </span>
                            ))}
                        </div>
                    </Reveal>
                    <Reveal delay={0.12} className="lg:col-span-5">
                        <div className="rounded-3xl bg-white border border-slate-200 shadow-xl shadow-blue-900/5 p-8 space-y-4">
                            <a data-testid="contact-phone-link" href={PHONE_HREF} className="flex items-center gap-4 group">
                                <span className="flex h-11 w-11 items-center justify-center rounded-full bg-blue-50 text-blue-700 group-hover:bg-blue-700 group-hover:text-white transition-colors"><Phone className="h-4 w-4" /></span>
                                <span>
                                    <span className="block text-xs uppercase tracking-[0.2em] text-slate-400">Call or Text</span>
                                    <span className="block font-semibold text-[#0A192F]">{PHONE}</span>
                                </span>
                            </a>
                            <a data-testid="contact-email-link" href={EMAIL_HREF} className="flex items-center gap-4 group">
                                <span className="flex h-11 w-11 items-center justify-center rounded-full bg-blue-50 text-blue-700 group-hover:bg-blue-700 group-hover:text-white transition-colors"><Mail className="h-4 w-4" /></span>
                                <span>
                                    <span className="block text-xs uppercase tracking-[0.2em] text-slate-400">Email</span>
                                    <span className="block font-semibold text-[#0A192F] break-all">{EMAIL}</span>
                                </span>
                            </a>
                            <BookLink
                                data-testid="contact-strip-book-button"
                                className="mt-2 flex items-center justify-center gap-2 rounded-full bg-blue-700 text-white text-sm font-semibold px-8 py-4 w-full transition-all duration-300 hover:bg-[#0A192F]"
                            >
                                <Calendar className="h-4 w-4" />
                                Book an Appointment
                            </BookLink>
                        </div>
                    </Reveal>
                </div>
            </section>
        </main>
    );
}
